import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import api from "../lib/api";
import ScoreBadge from "../components/ScoreBadge";
import LeadDrawer from "../components/LeadDrawer";
import { Button } from "../components/ui/button";
import { ExternalLink, Linkedin, Sparkles } from "lucide-react";
import { toast } from "sonner";

const STAGE_COLS = [
  { id: "not_started", label: "Not started", hint: "Start with a profile view" },
  { id: "profile_viewed", label: "Viewed", hint: "Send connect next" },
  { id: "connect_sent", label: "Connect sent", hint: "Awaiting acceptance" },
  { id: "connected", label: "Connected", hint: "Engage with a post or DM" },
  { id: "engaged", label: "Engaged", hint: "Like / comment logged" },
  { id: "dm_sent", label: "DM sent", hint: "Awaiting reply" },
  { id: "replied", label: "Replied", hint: "Move to meeting ask" },
];

export default function LinkedInPage() {
  const [board, setBoard] = useState({});
  const [loading, setLoading] = useState(true);
  const [focus, setFocus] = useState(null);
  const [allLeads, setAllLeads] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [b, l] = await Promise.all([
        api.get("/linkedin/board"),
        api.get("/leads"),
      ]);
      setBoard(b.data);
      setAllLeads(l.data);
    } catch {
      toast.error("Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openLead = (id) => {
    const l = allLeads.find((x) => x.lead_id === id);
    if (l) setFocus(l);
  };

  return (
    <Layout
      title="LinkedIn"
      subtitle="Workflow"
      actions={
        <Button variant="outline" onClick={load} data-testid="li-refresh" style={{ borderRadius: 2 }}>
          <Sparkles className="h-4 w-4 mr-1" strokeWidth={1.5} /> Refresh
        </Button>
      }
    >
      <div className="bg-card border border-border p-5 mb-6 flex items-start gap-4" style={{ borderRadius: 2 }}>
        <Linkedin className="h-5 w-5 text-primary mt-1" strokeWidth={1.5} />
        <div>
          <div className="font-serif text-xl tracking-tight" style={{ fontWeight: 450 }}>Manual workflow, AI-assisted.</div>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            LinkedIn doesn't allow automation, so YOU do the actions — the system tracks every touch, AI-drafts your connect notes &amp; DMs (copy-paste), and auto-pauses sequences the moment a CFO replies. Open a lead → LinkedIn tab → log each touch as you do it.
          </p>
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading…</div>}

      <div className="overflow-x-auto -mx-8 px-8 pb-4">
        <div className="flex gap-4 min-w-max">
          {STAGE_COLS.map((col) => {
            const items = board[col.id] || [];
            return (
              <div key={col.id} className="w-[280px] shrink-0 bg-secondary/40 border border-border" style={{ borderRadius: 2 }} data-testid={`li-col-${col.id}`}>
                <div className="px-4 py-3 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div className="overline">{col.label}</div>
                    <div className="font-mono text-xs">{items.length}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground/70 mt-1">{col.hint}</div>
                </div>
                <div className="p-2 space-y-2 min-h-[200px]">
                  {items.map((l) => (
                    <div
                      key={l.lead_id}
                      onClick={() => openLead(l.lead_id)}
                      className="bg-card border border-border p-3 cursor-pointer lift fade-up"
                      style={{ borderRadius: 2 }}
                      data-testid={`li-card-${l.lead_id}`}
                    >
                      <div className="font-medium text-sm truncate">{l.full_name}</div>
                      <div className="text-xs text-muted-foreground truncate">{l.title} · {l.company}</div>
                      <div className="text-xs text-muted-foreground/70 truncate">{l.industry}</div>
                      <div className="mt-2 flex items-center justify-between gap-2">
                        <ScoreBadge score={l.score} band={l.score_band} />
                        {l.linkedin_url && (
                          <a
                            href={l.linkedin_url}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-muted-foreground hover:text-primary transition-colors"
                            title="Open LinkedIn profile"
                            data-testid={`li-open-${l.lead_id}`}
                          >
                            <ExternalLink className="h-3 w-3" strokeWidth={1.5} />
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <div className="text-xs text-muted-foreground text-center py-6">—</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <LeadDrawer
        lead={focus}
        defaultTab="linkedin"
        onClose={() => setFocus(null)}
        onUpdated={(updated) => {
          setFocus(updated);
          setAllLeads((prev) => prev.map((x) => (x.lead_id === updated.lead_id ? updated : x)));
          load();
        }}
      />
    </Layout>
  );
}
